/*
  Packs LP v2 · add to cart with stacked discount codes (window.QOTTlp)

  Every card form [data-lp2-form] carries the grid's code in data-code.
  A ticked liner [data-lp2-liner] adds its variant and its own data-code.
  On submit:
    1. add the items in one /cart/add.js call
    2. attach every code in one /cart/update.js call, merged with the codes
       already on the cart (the `discount` field REPLACES the list, so we
       read first and merge, same as PDP v2)
    3. Buy X get Y grids: if no qualifying item is in the cart, show the
       grid's unlock note on the card
    4. fire qott:cart-updated (PDP Header v2 badge listens) and open the
       Rebuy Smart Cart, or go to /cart
  A failed code never blocks the add.

  The card forms have no action on purpose: Rebuy / the theme listen for
  forms posting to /cart/add and would add the product a second time.
*/
(() => {
    if (window.QOTTlp) return;

    const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const json = { 'Content-Type': 'application/json', 'Accept': 'application/json' };

    const getCart = async () => {
        const res = await fetch(root() + 'cart.js', { credentials: 'same-origin', headers: { 'Accept': 'application/json' } });
        return res.json();
    };

    const addItems = async (items) => {
        const res = await fetch(root() + 'cart/add.js', {
            method: 'POST', credentials: 'same-origin', headers: json, body: JSON.stringify({ items }),
        });
        const data = await res.json();
        if (!res.ok || data.status) throw new Error(data.description || data.message || 'Could not add to cart');
        return data;
    };

    const uniqueCodes = (codes) => {
        const seen = new Set();
        return codes.map(c => String(c || '').trim()).filter(c => {
            if (!c) return false;
            const k = c.toUpperCase();
            if (seen.has(k)) return false;
            seen.add(k);
            return true;
        });
    };

    // Attach several codes at once, keeping the ones already on the cart.
    const attachCodes = async (codes) => {
        const wanted = uniqueCodes(codes);
        const cart = await getCart();
        if (!wanted.length) return cart;
        const existing = uniqueCodes((cart.discount_codes || []).map(d => d && d.code));
        const have = new Set(existing.map(c => c.toUpperCase()));
        const missing = wanted.filter(c => !have.has(c.toUpperCase()));
        if (!missing.length) return cart;

        const res = await fetch(root() + 'cart/update.js', {
            method: 'POST', credentials: 'same-origin', headers: json,
            body: JSON.stringify({ discount: existing.concat(missing).join(',') }),
        });
        const updated = await res.json();
        if (!res.ok || updated.status) throw new Error(updated.description || updated.message || 'Could not apply discount code');
        missing.forEach(code => {
            const hit = (updated.discount_codes || []).find(d => d && d.code && d.code.toUpperCase() === code.toUpperCase());
            if (!hit || hit.applicable === false) console.info(`QOTTlp: code "${code}" is on the cart but not applicable yet`);
        });
        return updated;
    };

    const openCart = async () => {
        const rebuy = window.Rebuy;
        if (rebuy && rebuy.SmartCart && typeof rebuy.SmartCart.show === 'function') {
            if (rebuy.Cart && typeof rebuy.Cart.fetchCart === 'function') {
                try { await rebuy.Cart.fetchCart(); } catch (err) {}
            }
            rebuy.SmartCart.show();
            return;
        }
        window.location.href = root() + 'cart';
    };

    const hasQualifying = (cart, ids) => (cart.items || []).some(li => ids.includes(String(li.product_id)));

    /**
     * items: [{ id, quantity }], codes: ['CODE', ...]
     * Resolves with the cart. Rejects only when the add itself fails.
     */
    const add = async (items, codes = [], options = {}) => {
        await addItems(items);
        let cart = null;
        try { cart = await attachCodes(codes); } catch (err) { console.warn('QOTTlp: could not attach code(s)', err); }
        if (!cart) { try { cart = await getCart(); } catch (err) {} }
        if (cart) document.dispatchEvent(new CustomEvent('qott:cart-updated', { detail: { cart } }));
        if (options.openCart !== false) await openCart();
        return cart;
    };

    const setPrice = (form, pill) => {
        const priceEl = form.querySelector('[data-lp2-price]');
        if (!priceEl || !pill) return;
        const now = pill.dataset.now || '';
        const was = pill.dataset.was || '';
        priceEl.innerHTML = `<span class="lp2-now">${now}</span>${was ? ` <s class="lp2-was">${was}</s>` : ''}`;
    };

    const bindForm = (form) => {
        if (!form || form.dataset.lp2Bound) return;
        form.dataset.lp2Bound = 'true';

        const idInput = form.querySelector('[data-lp2-id]');
        const button = form.querySelector('[data-lp2-submit]');
        const label = button && button.querySelector('.qv2-btn__label');
        const note = form.querySelector('[data-lp2-note]');
        const grid = form.closest('[data-lp2-grid]');

        form.querySelectorAll('[data-lp2-variant]').forEach(pill => {
            pill.addEventListener('click', () => {
                form.querySelectorAll('[data-lp2-variant]').forEach(p => {
                    p.classList.toggle('is-selected', p === pill);
                    p.setAttribute('aria-checked', p === pill ? 'true' : 'false');
                });
                if (idInput) idInput.value = pill.dataset.lp2Variant;
                setPrice(form, pill);
                const available = pill.dataset.available === 'true';
                if (button) button.disabled = !available;
                if (label) label.textContent = available ? (button.dataset.label || 'Add to cart') : 'Sold out';
            });
        });

        form.addEventListener('submit', async (e) => {
            e.preventDefault();
            e.stopImmediatePropagation();
            if (!idInput || !idInput.value || (button && button.classList.contains('is-loading'))) return;
            if (note) { note.hidden = true; note.textContent = ''; note.classList.remove('is-error'); }

            const items = [{ id: Number(idInput.value), quantity: 1 }];
            const codes = [form.dataset.code];
            const liner = form.querySelector('[data-lp2-liner]');
            if (liner && liner.checked && liner.value) {
                items.push({ id: Number(liner.value), quantity: 1 });
                codes.push(liner.dataset.code);
            }

            if (button) button.classList.add('is-loading');
            try {
                const cart = await add(items, codes, { openCart: false });
                const unlockIds = ((grid && grid.dataset.unlockIds) || '').split(',').filter(Boolean);
                if (unlockIds.length && cart && !hasQualifying(cart, unlockIds) && note) {
                    note.textContent = grid.dataset.unlockNote || '';
                    note.hidden = !note.textContent;
                }
                if (liner) liner.checked = false;
                await openCart();
            } catch (err) {
                console.error('QOTTlp: add to cart failed', err);
                if (note) {
                    note.textContent = err.message || 'Something went wrong adding this to your cart.';
                    note.classList.add('is-error');
                    note.hidden = false;
                }
            } finally {
                if (button) button.classList.remove('is-loading');
            }
        });
    };

    // Smooth jump for finder chips and #shop buttons, with a short highlight.
    const bindJumps = () => {
        document.querySelectorAll('a[data-lp2-jump]').forEach(a => {
            if (a.dataset.lp2JumpBound) return;
            a.dataset.lp2JumpBound = 'true';
            a.addEventListener('click', (e) => {
                const hash = (a.getAttribute('href') || '').split('#')[1];
                const target = hash && document.getElementById(hash);
                if (!target) return;
                e.preventDefault();
                target.scrollIntoView({ behavior: 'smooth', block: target.classList.contains('lp2-card') ? 'center' : 'start' });
                if (target.classList.contains('lp2-card')) {
                    target.classList.remove('is-flash');
                    void target.offsetWidth;
                    target.classList.add('is-flash');
                }
                try { history.replaceState(null, '', '#' + hash); } catch (err) {}
            });
        });
    };

    const bindAll = () => {
        document.querySelectorAll('form[data-lp2-form]').forEach(bindForm);
        bindJumps();
    };

    window.QOTTlp = { add, attachCodes, getCart, openCart, bindAll };

    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bindAll);
    else bindAll();
    document.addEventListener('shopify:section:load', bindAll);
})();